// Anonymous UI-interaction telemetry (mig 805 ui_events) — the substrate the
// weekly UX-advisor routine reads to suggest UI improvements.
//
// PRIVACY CONTRACT (POPIA minimization — do not loosen without a privacy-
// policy update): events carry the per-tab session id (shared with
// client_errors) and the normalised screen path. tenant_id and role are
// derived SERVER-side by the ui_events trigger — never sent from here.
// Metadata never contains user ids, names, error message text, or field
// values: labels are digit-stripped and truncated.
//
// Loss is acceptable by design: in-memory buffer, flushed on 50 events /
// 60s / tab-hide, with a small localStorage carryover resent on next boot.
// This deliberately does NOT use offlineQueue (that is durability machinery
// for operational writes and feeds the operator-facing queue badge).
//
// Kill switches: localStorage.setItem('kf49_ui_telemetry', 'off') per
// device; VITE_UI_TELEMETRY='off' per build; emergency global stop =
// REVOKE EXECUTE ON FUNCTION ingest_ui_events(JSONB) FROM authenticated;
// (one-line migration — since mig 833 ingestion goes through that RPC,
// the table's INSERT policy no longer gates this path).
//
// INGESTION IS AN RPC, NOT A TABLE WRITE (mig 833): the original PostgREST
// upsert (INSERT ... ON CONFLICT DO NOTHING) was rejected by RLS on every
// batch — ON CONFLICT arbitration needs SELECT access and ui_events has no
// SELECT policy by privacy design — so the sink was dead from day one and
// the catch-all below hid it for 30 hours. ingest_ui_events keeps the
// event_id dedupe server-side; the identity trigger still forces
// tenant/role. If a flush fails now, the FIRST failure per session is
// reported to client_errors so a dead sink can never be silent again.

import { supabase } from '@/lib/supabase'
import { sessionId, reportError, isSwallowed } from '@/lib/errorReporter'
import { newUuid } from '@/lib/uuid'

interface UiEvent {
  event_id:    string
  session_id:  string
  event_type:  'page_view' | 'page_exit' | 'mutation_error' | 'slow_save' | 'save_timeout' | 'error_dialog' | 'reload' | 'query_error'
  screen:      string
  metadata:    Record<string, unknown> | null
  occurred_at: string
}

const FLUSH_AT_EVENTS  = 50
const FLUSH_INTERVAL   = 60_000
const SLOW_SAVE_MS     = 5_000
const CARRYOVER_KEY    = 'kf49_ui_telemetry_carryover'
const CARRYOVER_CAP    = 200
const KILL_KEY         = 'kf49_ui_telemetry'

// Pre-auth / public surfaces — never even buffered (RLS would reject them
// anyway: no active tenant => the identity trigger raises).
const EXCLUDED_PREFIXES = [
  '/login', '/signup', '/accept-invite', '/clock-face', '/g/', '/portal/',
  '/payslip-share/', '/worker-portal/', '/privacy',
]

let buffer: UiEvent[] = []
let installed = false
let flushTimer: ReturnType<typeof setInterval> | null = null
// Cached for the keepalive hide-path flush (sendBeacon can't set the
// Authorization header supabase RLS needs, so we use fetch keepalive).
let cachedToken: string | null = null

// Dwell accounting for the current screen.
let currentScreen: string | null = null
let enteredAt = 0
let hiddenAccum = 0
let hiddenSince: number | null = null

export function telemetryEnabled(): boolean {
  if (import.meta.env.VITE_UI_TELEMETRY === 'off') return false
  try { return localStorage.getItem(KILL_KEY) !== 'off' } catch { return true }
}

// --- screen normalisation ---------------------------------------------------

// '/pallets/KF49-2026-12345/edit' -> '/pallets/:id/edit'. Any segment that is
// a UUID, all digits, or >=6 chars containing a digit is an identifier.
export function normaliseScreen(pathname: string): string {
  const segs = pathname.split('/').filter(Boolean).slice(0, 4).map(s => {
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) return ':id'
    if (/^\d+$/.test(s)) return ':id'
    if (s.length >= 6 && /\d/.test(s)) return ':id'
    return s
  })
  return ('/' + segs.join('/')).slice(0, 120)
}

function excluded(pathname: string): boolean {
  return EXCLUDED_PREFIXES.some(p => pathname === p || pathname.startsWith(p))
}

// Labels reach the DB — strip digits (ids, weights, amounts) and truncate so
// a label can never smuggle a value or a name-with-number through.
function cleanLabel(s: string | undefined | null): string | null {
  if (!s) return null
  const cleaned = s.replace(/\d+/g, '').replace(/\s+/g, ' ').trim().slice(0, 80)
  return cleaned || null
}

// --- core enqueue / flush ----------------------------------------------------

function push(event_type: UiEvent['event_type'], screen: string, metadata: Record<string, unknown> | null): void {
  if (!telemetryEnabled()) return
  buffer.push({
    // newUuid, not crypto.randomUUID: this runs on EVERY route change, and the
    // bare call threw the shell at /login on Chrome 86 (Sentry C0/BT).
    event_id:    newUuid(),
    session_id:  sessionId(),
    event_type,
    screen,
    metadata,
    occurred_at: new Date().toISOString(),
  })
  if (buffer.length >= FLUSH_AT_EVENTS) void flush()
}

// One report per session: telemetry must never spam the error sink, but a
// permanently-failing flush must also never be invisible again.
let flushFailureReported = false

async function flush(): Promise<void> {
  if (buffer.length === 0) return
  const rows = buffer
  buffer = []
  try {
    // Server-side ON CONFLICT (event_id) DO NOTHING — safe to resend.
    const { error } = await supabase.rpc('ingest_ui_events', { p_events: rows })
    if (error) throw error
  } catch (e) {
    if (!flushFailureReported) {
      flushFailureReported = true
      void reportError(e, { source: 'manual', context: { kind: 'ui_telemetry_flush', batch: rows.length } })
    }
    // Offline / rejected / anything: put them back (capped, drop-oldest).
    buffer = [...rows, ...buffer].slice(-CARRYOVER_CAP)
  }
}

// Tab-hide path: fire-and-forget keepalive fetch (the page may be gone before
// a normal request settles), then snapshot to localStorage as the backstop —
// the next boot resends; event_id dedupe makes the double-send harmless.
function flushOnHide(): void {
  if (buffer.length === 0) return
  const rows = buffer
  buffer = []
  try {
    localStorage.setItem(CARRYOVER_KEY,
      JSON.stringify([...readCarryover(), ...rows].slice(-CARRYOVER_CAP)))
  } catch { /* quota — best effort */ }
  if (!cachedToken) return
  try {
    void fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/ingest_ui_events`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type':  'application/json',
        apikey:          import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        Authorization:   `Bearer ${cachedToken}`,
      },
      body: JSON.stringify({ p_events: rows }),
    })
  } catch { /* best effort */ }
}

function readCarryover(): UiEvent[] {
  try {
    const raw = localStorage.getItem(CARRYOVER_KEY)
    return raw ? (JSON.parse(raw) as UiEvent[]) : []
  } catch { return [] }
}

async function resendCarryover(): Promise<void> {
  const rows = readCarryover()
  if (rows.length === 0) return
  try {
    const { error } = await supabase.rpc('ingest_ui_events', { p_events: rows })
    if (!error) localStorage.removeItem(CARRYOVER_KEY)
  } catch { /* retry next boot */ }
}

// --- event constructors -------------------------------------------------------

export function trackRouteChange(pathname: string): void {
  if (!telemetryEnabled()) return
  if (excluded(pathname)) { currentScreen = null; return }
  const screen = normaliseScreen(pathname)
  if (screen === currentScreen) return
  const now = performance.now()
  const meta: Record<string, unknown> = {}
  if (currentScreen) {
    meta.from_screen   = currentScreen
    meta.from_dwell_ms = Math.max(0, Math.round(now - enteredAt - hiddenAccum))
  }
  currentScreen = screen
  enteredAt = now
  hiddenAccum = 0
  hiddenSince = document.visibilityState === 'hidden' ? now : null
  push('page_view', screen, Object.keys(meta).length ? meta : null)
}

// PG/PostgREST code off an error, surviving one wrap: save paths routinely
// rethrow as plain Error (friendlyError output, "draft saved but…" chains),
// which strips `.code` — 68 worker-save mutation_errors landed code-null in
// one 14-day pull and were undiagnosable. `cause` is the standard place a
// wrapper keeps the original; check it before giving up.
function pgCode(err: unknown): string | undefined {
  const e = err as { code?: unknown; cause?: { code?: unknown } } | null
  const c = typeof e?.code === 'string' ? e.code : (typeof e?.cause?.code === 'string' ? e.cause.code : undefined)
  return c ? c.slice(0, 12) : undefined
}

export function trackMutationError(err: unknown, mutationKey?: unknown): void {
  if (!currentScreen) return
  const e = err as { name?: string } | null
  push('mutation_error', currentScreen, {
    validation: e?.name === 'ValidationError',
    code:       pgCode(err),
    label:      cleanLabel(Array.isArray(mutationKey) ? String(mutationKey[0]) : undefined) ?? undefined,
  })
}

// Read-path failures (mig 20260719220000) — before this, a screen whose list
// query errored scored zero friction (query errors went to client_errors
// only) and the 2026-07-19 coverage audit flagged it as the biggest capture
// gap. Two guards keep the signal honest:
// - isSwallowed drops transient network noise (offline "Failed to fetch"/
//   "Load failed", auth-lock races): a bad link is connectivity, not UI
//   friction, and iOS-on-cellular would otherwise dominate the metric.
// - One event per screen+label per minute: a 15s poller stuck on a dead
//   endpoint re-errors every cycle; unthrottled it would swamp the sink.
const QUERY_ERROR_THROTTLE_MS = 60_000
const lastQueryError = new Map<string, number>()
// Burst collapse. Deliberately a MUCH shorter window than the per-label
// throttle: the shape we're suppressing is a screen's mount-time queries all
// failing together (live: 8 labels inside the same SECOND), not a query that
// keeps failing over minutes. A stuck poller retrying every 30s therefore
// still lands in its own window and stays visible — only the simultaneous pile
// collapses.
const QUERY_ERROR_BURST_MS  = 5_000
const QUERY_ERROR_BURST_MAX = 3
const queryErrorBursts = new Map<string, { since: number; n: number }>()

// True when this screen+label already fired within the window. Marks the
// key as fired otherwise. The size cap stops unbounded growth on a tablet
// that lives for weeks without a reload.
function queryErrorThrottled(key: string, now: number): boolean {
  const last = lastQueryError.get(key)
  if (last != null && now - last < QUERY_ERROR_THROTTLE_MS) return true
  if (lastQueryError.size >= 200) lastQueryError.clear()
  lastQueryError.set(key, now)
  return false
}

// Per-screen burst cap: at most QUERY_ERROR_BURST_MAX events per screen per
// throttle window, regardless of how many distinct queries failed. Returns true
// when this one should be dropped. Deliberately counts events we RECORDED, not
// events we saw, so the window slides from the first recorded error.
function queryErrorBurst(screen: string, now: number): boolean {
  const b = queryErrorBursts.get(screen)
  if (b == null || now - b.since >= QUERY_ERROR_BURST_MS) {
    if (queryErrorBursts.size >= 200) queryErrorBursts.clear()
    queryErrorBursts.set(screen, { since: now, n: 1 })
    return false
  }
  if (b.n >= QUERY_ERROR_BURST_MAX) return true
  b.n += 1
  return false
}

// The two suppressions, in one place so trackQueryError stays a straight line
// (fallow CRAP gate — same reason queryErrorThrottled was extracted for #857).
//
// Per-label throttle stops ONE stuck poller swamping the metric. It does not
// stop the commoner shape: a single network/auth stumble fails every query a
// screen mounted, and each distinct label lands in its own throttle bucket.
// Live example (2026-07-28 pull, session a6b019c2 at 06:58:05) — eight of the
// dashboard's mount-time queries erroring in the SAME SECOND, recorded as eight
// friction points. reportError collapsed them to nothing because it dedupes on
// the message and all eight carried the identical one; that is the tell that it
// was one incident, not eight. Fleet-wide this was 295 of 376 query_errors,
// enough to bury real per-query findings.
//
// So cap the screen as well as the label. A genuine multi-query outage still
// registers — it just registers as an incident rather than as a proportional
// pile, and the burst shape stays legible in the day/session histogram.
function queryErrorSuppressed(screen: string, label: string | null, now: number): boolean {
  if (queryErrorBurst(screen, now)) return true
  return queryErrorThrottled(`${screen}|${label ?? ''}`, now)
}

export function trackQueryError(err: unknown, queryKey?: unknown): void {
  if (!currentScreen || isSwallowed(err)) return
  const code = pgCode(err)
  // PGRST303 = JWT expired: a waking tablet's poll racing the auth refresh.
  // It self-heals seconds later and says nothing about the SCREEN — 28 of
  // these were distorting the dashboard's friction score in one 14-day pull.
  // client_errors still records the incident; only the friction stamp is
  // dropped.
  if (code === 'PGRST303') return
  const label = cleanLabel(Array.isArray(queryKey) ? String(queryKey[0]) : undefined)
  if (queryErrorSuppressed(currentScreen, label, Date.now())) return
  push('query_error', currentScreen, {
    code,
    label: label ?? undefined,
  })
}

export function trackSlowSave(label: string, elapsedMs: number): void {
  if (!currentScreen) return
  push('slow_save', currentScreen, { label: cleanLabel(label), elapsed_ms: Math.round(elapsedMs) })
}

export function trackSaveTimeout(label: string, timeoutMs: number): void {
  if (!currentScreen) return
  push('save_timeout', currentScreen, { label: cleanLabel(label), timeout_ms: timeoutMs })
}

export function trackErrorDialog(title: string | undefined): void {
  if (!currentScreen) return
  push('error_dialog', currentScreen, { label: cleanLabel(title) })
}

export { SLOW_SAVE_MS }

// --- install -------------------------------------------------------------------

export function installUiTelemetry(): void {
  if (installed || typeof window === 'undefined') return
  installed = true

  // Token cache for the keepalive hide-path (async getSession can't run
  // during pagehide).
  void supabase.auth.getSession().then(({ data }) => {
    cachedToken = data.session?.access_token ?? null
  })
  supabase.auth.onAuthStateChange((_ev, session) => {
    cachedToken = session?.access_token ?? null
  })

  flushTimer = setInterval(() => void flush(), FLUSH_INTERVAL)
  void flushTimer // (kept for symmetry; never torn down — app-lifetime singleton)

  document.addEventListener('visibilitychange', () => {
    const now = performance.now()
    if (document.visibilityState === 'hidden') {
      // Just flush — page_exit only fires on pagehide (a hidden tab usually
      // comes back; emitting dwell here would duplicate the sample the next
      // page_view carries).
      hiddenSince = now
      flushOnHide()
    } else if (hiddenSince != null) {
      hiddenAccum += now - hiddenSince
      hiddenSince = null
    }
  })

  window.addEventListener('pagehide', () => {
    const now = performance.now()
    if (currentScreen && telemetryEnabled()) {
      push('page_exit', currentScreen, {
        from_dwell_ms: Math.max(0, Math.round(now - enteredAt - (hiddenAccum + (hiddenSince != null ? now - hiddenSince : 0)))),
      })
      // bfcache restore: dwell restarts so a later page_view doesn't
      // re-report the segment this page_exit already carried.
      enteredAt = now
      hiddenAccum = 0
    }
    flushOnHide()
  })

  // Reload-frequency signal (mig 833): one event when this boot was a
  // browser reload. Operators refreshing to un-stick the app IS the
  // friction this pipeline exists to measure — the 2026-06-11 staleness
  // and hang investigations had zero data on which screens force reloads.
  // ui_friction_summary counts these per screen; watch them drop as the
  // fixes land. (navigation type 'navigate'/'back_forward' = not friction.)
  //
  // UPDATE-vs-FRICTION split (mig 836): a reload that crosses BUILDS is the
  // update banner doing its job (or the SW takeover path), not operator
  // pain — on 2026-06-12, 4 morning deploys produced banner reloads that
  // swamped the friction metric. Each TAB tracks the build it last booted
  // (sessionStorage: per-tab, survives reload, isolated between tabs — a
  // shared localStorage marker would mis-tag the second tab of a pair).
  // Build changed across the reload => metadata {update:true}; the summary
  // counts those separately and keeps them out of friction scoring.
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    const build = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev'
    let prevBuild: string | null = null
    try {
      prevBuild = sessionStorage.getItem('kf49_tab_build')
      sessionStorage.setItem('kf49_tab_build', build)
    } catch { /* storage unavailable — treat as unknown */ }
    if (nav?.type === 'reload' && telemetryEnabled() && !excluded(window.location.pathname)) {
      const isUpdate = prevBuild !== null && prevBuild !== build
      push('reload', normaliseScreen(window.location.pathname), isUpdate ? { update: true } : null)
    }
  } catch { /* best effort */ }

  void resendCarryover()
}
